/**
 * @param {[number, number]} p0
 * @param {[number, number]} p1
 * @param {number} angle Rad along wall p0→p1
 * @param {'left' | 'right'} hingeSide
 * @param {'inside' | 'outside'} swingSide
 * @param {'open' | 'closed' | 'frame-only'} [doorState]
 * @returns {{
 *   hinge: [number, number];
 *   leafEnd: [number, number] | null;
 *   arc: { x: number; y: number; radius: number; rotationDeg: number; angleDeg: number } | null;
 *   hitBounds: { minX: number; maxX: number; minY: number; maxY: number };
 * }}
 */
export function buildDoorSwing2DGeometry({
  p0,
  p1,
  angle,
  hingeSide = 'left',
  swingSide = 'inside',
  doorState = 'closed',
}) {
  const widthPx = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  const radius = Math.max(widthPx, 1);
  const wallUx = Math.cos(angle);
  const wallUy = Math.sin(angle);
  const perpInX = -wallUy;
  const perpInY = wallUx;
  const perpOutX = wallUy;
  const perpOutY = -wallUx;

  const hinge = hingeSide === 'left' ? p0 : p1;
  const closedDirX = hingeSide === 'left' ? wallUx : -wallUx;
  const closedDirY = hingeSide === 'left' ? wallUy : -wallUy;
  const swingPerpX = swingSide === 'inside' ? perpInX : perpOutX;
  const swingPerpY = swingSide === 'inside' ? perpInY : perpOutY;

  const openingXs = [p0[0], p1[0], hinge[0]];
  const openingYs = [p0[1], p1[1], hinge[1]];

  if (doorState === 'frame-only') {
    const pad = Math.max(radius * 0.08, 2);
    return {
      hinge,
      leafEnd: null,
      arc: null,
      hitBounds: {
        minX: Math.min(...openingXs) - pad,
        maxX: Math.max(...openingXs) + pad,
        minY: Math.min(...openingYs) - pad,
        maxY: Math.max(...openingYs) + pad,
      },
    };
  }

  if (doorState === 'closed') {
    const leafEnd = [hinge[0] + closedDirX * radius, hinge[1] + closedDirY * radius];
    const pad = Math.max(radius * 0.08, 2);
    return {
      hinge,
      leafEnd,
      arc: null,
      hitBounds: {
        minX: Math.min(...openingXs, leafEnd[0]) - pad,
        maxX: Math.max(...openingXs, leafEnd[0]) + pad,
        minY: Math.min(...openingYs, leafEnd[1]) - pad,
        maxY: Math.max(...openingYs, leafEnd[1]) + pad,
      },
    };
  }

  const leafEnd = [hinge[0] + swingPerpX * radius, hinge[1] + swingPerpY * radius];
  const closedAngleDeg = (Math.atan2(closedDirY, closedDirX) * 180) / Math.PI;
  const arc = {
    x: hinge[0],
    y: hinge[1],
    radius,
    rotationDeg: closedAngleDeg,
    angleDeg: swingSide === 'inside' ? (hingeSide === 'left' ? 90 : -90) : (hingeSide === 'left' ? -90 : 90),
  };

  const xs = [hinge[0], leafEnd[0], p0[0], p1[0]];
  const ys = [hinge[1], leafEnd[1], p0[1], p1[1]];
  const arcPad = radius * 0.15;
  return {
    hinge,
    leafEnd,
    arc,
    hitBounds: {
      minX: Math.min(...xs) - arcPad,
      maxX: Math.max(...xs) + arcPad,
      minY: Math.min(...ys) - arcPad,
      maxY: Math.max(...ys) + arcPad,
    },
  };
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerOpening} opening
 * @returns {{ hingeSide: 'left' | 'right'; swingSide: 'inside' | 'outside'; doorState: 'open' | 'closed' | 'frame-only' }}
 */
export function resolveDoorSwingFields(opening) {
  const hingeSide =
    opening.hingeSide === 'right' || opening.hingeSide === 'left'
      ? opening.hingeSide
      : opening.swingDirection === 'right'
        ? 'right'
        : 'left';
  const swingSide =
    opening.swingSide === 'outside' || opening.swingSide === 'inside'
      ? opening.swingSide
      : 'inside';
  const doorState =
    opening.doorState === 'open' ||
    opening.doorState === 'closed' ||
    opening.doorState === 'frame-only'
      ? opening.doorState
      : 'closed';
  return { hingeSide, swingSide, doorState };
}
